// react
import { ChangeEvent, Dispatch, MouseEvent, SetStateAction, useContext, useState } from 'react';

// types
import { Entity, EntityType, ErrorCause, Feedback } from '../../../@types';

// i18n
import { useTranslation } from 'react-i18next';

// context
import { ContextBanner } from '../../../App.context';
import { ContextProject } from '../../pages/Project/Project.context';

// mui
import { Alert, AlertTitle, Grid, SelectChangeEvent } from '@mui/material';

// fields
import { Input } from '../../fields/Input/Input';
import { InputPhone } from '../../fields/InputPhone/InputPhone';

// ui
import { UserPermissions } from '../../ui/UserPermissions/UserPermissions';

// utils
import {
	permissionsBeneficiary,
	permissionsReadOnly,
	permissionsReadWrite,
	permissionsSupplier,
} from '../../../utils/permissions';
import { entityTypesDefault } from '../../../@types/unions';

// props
export interface FormAddUserProps {
	entity?: Entity;
	onClose?: () => void;
	onSuccess?: () => void;
	setIsLoading: Dispatch<SetStateAction<boolean>>;
}

export const FormAddUser = ({ entity, onClose, onSuccess, setIsLoading }: FormAddUserProps) => {
	// hooks
	const { t } = useTranslation();

	// context
	const { setBanner } = useContext(ContextBanner);
	const { project } = useContext(ContextProject);

	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// vars
	const idForm = `form-add-user`;

	let permissionsDefault = {};

	const isEntityTypeDefault = entityTypesDefault.some((type: EntityType) => type === entity?.type);

	if (isEntityTypeDefault) {
		permissionsDefault = { ...permissionsReadWrite };
	} else if (entity?.type === 'BENEFICIARY') {
		permissionsDefault = { ...permissionsBeneficiary };
	} else if (entity?.type === 'SUPPLIER') {
		permissionsDefault = { ...permissionsSupplier };
	} else {
		permissionsDefault = { ...permissionsReadOnly };
	}

	// state
	const [feedback, setFeedback] = useState<Feedback | null>(null);
	const [values, setValues] = useState({
		country: String(project?.country?.id || ''),
		email: '',
		firstName: '',
		lastName: '',
		permissions: permissionsDefault,
		phone: '',
		title: '',
	});

	const handleChangeValue = (e: ChangeEvent | SelectChangeEvent<unknown>) => {
		const { name, value } = e.target as HTMLInputElement;
		setValues({
			...values,
			[name]: value,
		});
	};

	const handleSubmit = async (e: MouseEvent<HTMLFormElement>) => {
		e.preventDefault();

		try {
			// set loader
			setIsLoading(true);

			if (!values.email && !values.phone) {
				throw new Error(t('error.userEmailOrPhoneRequired'), {
					cause: {
						id: 'USER_PHONE_OR_EMAIL_EMPTY',
					},
				});
			}

			const fetchAddUser = await fetch(`${process.env.REACT_APP_API_URL}/users`, {
				method: 'POST',
				headers: {
					Authorization: `Bearer ${accessToken}`,
					User: String(idToken),
				},
				body: JSON.stringify({
					country: values.country ? Number(values.country) : null,
					email: values.email || null,
					entities: entity ? [entity.id] : [],
					firstName: values.firstName,
					lastName: values.lastName,
					phone: values.phone ? Number(values.phone) : null,
					projects: [
						{
							id: project?.id,
							permissions: {
								...values.permissions,
							},
							status: 'INVITED',
						},
					],
					role: 'GENERAL',
					title: values.title,
				}),
			});
			const resAddUser = await fetchAddUser.json();

			if (resAddUser.error) {
				throw new Error(resAddUser.error.message, {
					cause: {
						details: resAddUser.error.details,
						id: resAddUser.error.id,
					},
				});
			}

			if (onSuccess) {
				onSuccess();
			}

			// set success banner
			setBanner({
				hasClose: true,
				message: t('alert.userAdded'),
				severity: 'success',
			});

			// close dialog
			if (onClose) {
				onClose();
			}
		} catch (error) {
			const err = error as Error;
			const cause = err.cause as ErrorCause;

			const feedbackNew = {
				alert: {
					children: t('error.default'),
				},
			};

			if (cause.id === 'B2B_USER_NOT_PERMITTED') {
				feedbackNew.alert.children = t('error.userCreatedButNotInvited');

				if (onSuccess) {
					onSuccess();
				}
			}

			if (cause.id === 'USER_DUPLICATE') {
				if (cause.details.email) {
					feedbackNew.alert.children = t('error.userDuplicateEmail', {
						email: cause.details.email,
					});
				} else if (cause.details.phone) {
					feedbackNew.alert.children = t('error.userDuplicatePhone', {
						phone: cause.details.phone,
					});
				} else {
					feedbackNew.alert.children = t('error.userDuplicate');
				}
			}

			if (cause.id === 'USER_PHONE_OR_EMAIL_EMPTY') {
				feedbackNew.alert.children = t('error.userEmailOrPhoneRequired');
			}

			if (cause.id === 'USER_INVALID_ROLE') {
				feedbackNew.alert.children = t('error.userInvalidRole');
			}

			// set error
			setFeedback(feedbackNew);
		} finally {
			// set loader
			setIsLoading(false);
		}
	};

	return (
		<form id="form-add-user" name="formAddUser" onSubmit={handleSubmit}>
			<Grid container spacing={2}>
				<Grid item xs={12} md={6}>
					<Input
						id={`${idForm}-first-name`}
						label={t('firstName')}
						name="firstName"
						onChange={handleChangeValue}
						required={true}
						value={values.firstName}
					/>
				</Grid>
				<Grid item xs={12} md={6}>
					<Input
						id={`${idForm}-last-name`}
						label={t('lastName')}
						name="lastName"
						onChange={handleChangeValue}
						required={true}
						value={values.lastName}
					/>
				</Grid>
				<Grid item xs={12} md={6}>
					<Input
						id={`${idForm}-email`}
						label={t('email')}
						name="email"
						onChange={handleChangeValue}
						required={true}
						type="email"
						value={values.email}
					/>
				</Grid>
				<Grid item xs={12} md={6}>
					<InputPhone
						InputProps={{
							id: `${idForm}-phone`,
							name: 'phone',
							onChange: handleChangeValue,
							value: values.phone,
						}}
						label={t('phone')}
						SelectProps={{
							id: `${idForm}-country`,
							name: 'country',
							onChange: handleChangeValue,
							value: values.country,
						}}
					/>
				</Grid>
				<Grid item xs={12}>
					<Input
						id={`${idForm}-title`}
						label={t('jobTitle')}
						name="title"
						onChange={handleChangeValue}
						value={values.title}
					/>
				</Grid>
				{!['BENEFICIARY', 'SUPPLIER'].includes(entity?.type as string) && (
					<Grid item xs={12}>
						<UserPermissions
							defaultPermissions={permissionsDefault}
							defaultTemplate="CUSTOM"
							onChange={(e, permissions) => {
								setValues((prev) => ({
									...prev,
									permissions,
								}));
							}}
						/>
					</Grid>
				)}
				{feedback && (
					<Grid item xs={12}>
						<Alert severity="error" {...feedback.alert}>
							{feedback.title && <AlertTitle {...feedback.title} />}
							{feedback.alert.children}
						</Alert>
					</Grid>
				)}
			</Grid>
		</form>
	);
};
